/**
 * Chart Theme Configuration
 * Beautiful color schemes and styling for charts
 */

import type { ChartTheme } from '../types';

/**
 * Default chart theme with stunning gradients
 */
export const defaultChartTheme: ChartTheme = {
    // Primary colors
    primary: '#6366F1',      // Indigo
    secondary: '#8B5CF6',    // Purple
    success: '#10B981',      // Green
    error: '#EF4444',        // Red
    warning: '#F59E0B',      // Amber

    // Chart colors
    text: '#6B7280',         // Gray-500
    background: '#FFFFFF',   // White
    grid: '#E5E7EB',         // Gray-200

    // Away/Home specific colors
    away: '#FF6B6B',         // Coral Red
    home: '#4ECDC4',         // Teal
};

/**
 * Dark theme variant
 */
export const darkChartTheme: ChartTheme = {
    primary: '#818CF8',      // Light Indigo
    secondary: '#A78BFA',    // Light Purple
    success: '#34D399',      // Light Green
    error: '#F87171',        // Light Red
    warning: '#FBBF24',      // Light Amber

    text: '#D1D5DB',         // Gray-300
    background: '#1F2937',   // Gray-800
    grid: '#374151',         // Gray-700

    away: '#FF8787',         // Lighter Coral
    home: '#6EE7E0',         // Lighter Teal
};

/**
 * Gradient configurations for stunning visuals
 */
export const gradients = {
    away: ['rgba(255, 107, 107, 0.4)', 'rgba(255, 107, 107, 0.01)'],     // Red gradient
    home: ['rgba(78, 205, 196, 0.4)', 'rgba(78, 205, 196, 0.01)'],       // Teal gradient
    profit: ['rgba(16, 185, 129, 0.4)', 'rgba(16, 185, 129, 0.01)'],     // Green gradient
    loss: ['rgba(239, 68, 68, 0.4)', 'rgba(239, 68, 68, 0.01)'],         // Red gradient
    neutral: ['rgba(99, 102, 241, 0.4)', 'rgba(99, 102, 241, 0.01)'],    // Indigo gradient
};

/**
 * Line chart styling presets
 */
export const lineChartStyles = {
    default: {
        thickness: 2.5,
        curved: true,
        showDataPoints: false,
        animationDuration: 800,
    },
    bold: {
        thickness: 3.5,
        curved: true,
        showDataPoints: true,
        animationDuration: 800,
    },
    minimal: {
        thickness: 1.5,
        curved: false,
        showDataPoints: false,
        animationDuration: 400,
    },
    dots: {
        thickness: 2,
        curved: true,
        showDataPoints: true,
        animationDuration: 800,
    },
};

/**
 * Get away/home gradient colors
 */
export const getAwayHomeGradients = () => ({
    away: {
        color: defaultChartTheme.away,
        gradient: true,
        gradientColors: gradients.away as [string, string],
    },
    home: {
        color: defaultChartTheme.home,
        gradient: true,
        gradientColors: gradients.home as [string, string],
    },
});

/**
 * Chart spacing and dimensions
 */
export const chartDimensions = {
    defaultHeight: 300,
    mobileHeight: 250,
    defaultSpacing: 20,
    xAxisLabelCount: 5,
    yAxisLabelCount: 6,
};
